import { Link } from "react-router-dom";
import { ArrowRight, ArrowUpRight, Globe, TrendingUp, Briefcase, Settings } from "lucide-react";
import SectionHeading from "@/components/SectionHeading";
import Reveal from "@/components/Reveal";
import CTABanner from "@/components/CTABanner";
import FAQ from "@/components/FAQ";
import HeroBrandMark from "@/components/HeroBrandMark";

const DIVISIONS = [
  {
    n: "01",
    icon: Globe,
    name: "Toulios Growth Systems",
    flagship: true,
    summary: "Modern website, lead capture, CRM, dashboards, AI support and outsourced execution, built and operated for you.",
    components: [
      "Modern, conversion-optimised website",
      "Multi-channel lead capture & routing",
      "CRM with automated follow-up workflows",
      "Real-time KPI dashboards",
      "Custom-trained AI assistant",
      "Outsourced day-to-day execution team",
    ],
    outcome: "A business that compounds while the owner is on the tools.",
    link: { to: "/growth-system", label: "Explore the flagship" },
  },
  {
    n: "02",
    icon: TrendingUp,
    name: "Toulios Financial Strategy",
    flagship: false,
    summary: "Financial structuring, cashflow optimisation, margin diagnostics, and debt and capital strategy for established operators.",
    components: [
      "13-week cash plan & rolling forecast",
      "Margin & cost-to-serve diagnostics",
      "Capital structure & debt strategy",
      "Pricing & profitability modelling",
      "Board-grade financial reporting",
    ],
    outcome: "Clear visibility on cash, margin and capital, and the decisions they support.",
    link: { to: "/contact", label: "Discuss your financials" },
  },
  {
    n: "03",
    icon: Briefcase,
    name: "Toulios Revenue & Sales",
    flagship: false,
    summary: "Pricing strategy, sales process design, conversion improvement, pipeline optimisation and follow-up systems that win the work.",
    components: [
      "Pricing & packaging audit",
      "Sales process redesign",
      "Quote & proposal workflow",
      "Pipeline architecture & metrics",
      "Win-rate & conversion uplift",
    ],
    outcome: "Predictable pipeline, higher conversion, less leakage between marketing spend and revenue.",
    link: { to: "/contact", label: "Talk revenue & sales" },
  },
  {
    n: "04",
    icon: Settings,
    name: "Toulios Operations",
    flagship: false,
    summary: "Process design, SOPs, outsourcing models, operational efficiency and admin execution systems.",
    components: [
      "Process mapping & SOPs",
      "Outsourcing & offshoring models",
      "Admin & ops execution systems",
      "Working capital release",
      "Performance & team-KPI design",
    ],
    outcome: "Operations that scale without proportional admin headcount.",
    link: { to: "/contact", label: "Talk operations" },
  },
];

const STACK = [
  { stage: "Entry", offer: "The Toulios Growth System", div: "Growth Systems · Division 01", note: "Productised, fixed-fee, 60-day build then ongoing run-mode. The fastest first win." },
  { stage: "Expansion", offer: "Cashflow & financial structuring", div: "Financial Strategy · Division 02", note: "Engaged once the Growth System is live and the numbers are visible." },
  { stage: "Expansion", offer: "Pricing & sales conversion", div: "Revenue & Sales · Division 03", note: "Usually triggered when leads outgrow the existing sales process." },
  { stage: "Expansion", offer: "SOPs, outsourcing & admin", div: "Operations · Division 04", note: "When the team is the constraint, not the demand." },
  { stage: "Long-term", offer: "The Operator Partnership", div: "Cross-division retained", note: "Integrated monthly engagement across multiple divisions. Acts as the client's external growth office." },
];

const FAQS = [
  { q: "Do I need to engage all four divisions?", a: "No. Most clients start with one, usually the flagship Growth System, and expand from there. The Strategy Call identifies where to start for the maximum first-90-day impact in your business." },
  { q: "Are the four divisions delivered by the same team?", a: "Yes. One accountable team, one contract, one point of contact. The divisional structure exists so engagements can be scoped tightly, not so you have to deal with four separate suppliers." },
  { q: "When will the second-stage division pages launch?", a: "We're productising them in phases as we build out the case-study base for each. In the meantime, the Strategy Call is the cleanest way to scope work in any of the second-stage divisions." },
  { q: "How is this different from a regular consultancy?", a: "Most consultants deliver advice and stop. We deliver advice, install the systems behind it, and operate them where required. Same firm, same team, same contract." },
];

export default function WhatWeDo() {
  return (
    <>
      <section className="container-x pt-24 md:pt-32 pb-12" data-testid="wwd-hero">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 items-center">
          <div className="lg:col-span-8">
            <div className="overline">What we do</div>
            <h1 className="mt-6 max-w-3xl font-display font-light tracking-tight text-balance text-5xl sm:text-6xl md:text-7xl lg:text-8xl leading-[1.02]">
              Four <span className="text-gold">levers</span>. One firm.
            </h1>
            <p className="mt-8 max-w-2xl text-zinc-300 leading-relaxed text-lg">
              Toulios Advisory Group is structured as four divisions, one per
              lever, delivered by a single accountable team. Most clients start
              with the flagship Growth System and expand into the others over time.
            </p>
            <div className="mt-10 flex flex-col sm:flex-row gap-3">
              <Link to="/contact" className="btn-gold" data-testid="wwd-cta-book">
                Book a Strategy Call <ArrowRight className="w-4 h-4" />
              </Link>
              <Link to="/growth-system" className="btn-ghost">
                See the flagship product
              </Link>
            </div>
          </div>
          <div className="hidden lg:flex lg:col-span-4 items-center justify-center">
            <HeroBrandMark variant="mark" />
          </div>
        </div>
      </section>

      {/* DIVISIONS DETAIL */}
      <section className="section-y" data-testid="wwd-divisions">
        <div className="container-x space-y-24 md:space-y-32">
          {DIVISIONS.map((d, i) => (
            <Reveal key={d.name}>
              <article
                id={d.name.toLowerCase().replace(/\s+/g, "-")}
                className="grid grid-cols-1 lg:grid-cols-12 gap-12 items-start scroll-mt-28"
                data-testid={`wwd-division-${i}`}
              >
                <div className="lg:col-span-5">
                  <div className="flex items-center gap-3">
                    <span className="overline">{d.n}</span>
                    {d.flagship && (
                      <span
                        data-testid={`wwd-flagship-badge-${i}`}
                        className="text-[10px] tracking-[0.22em] uppercase text-gold border border-gold/40 px-2 py-1"
                      >
                        Flagship
                      </span>
                    )}
                  </div>
                  <h2 className="mt-6 text-3xl md:text-4xl lg:text-5xl font-display font-light tracking-tight text-balance">
                    {d.name}
                  </h2>
                  <p className="mt-6 text-zinc-400 leading-relaxed">{d.summary}</p>
                  <Link to={d.link.to} className="btn-ghost mt-8" data-testid={`wwd-division-cta-${i}`}>
                    {d.link.label} <ArrowUpRight className="w-4 h-4" />
                  </Link>
                </div>

                <div className="lg:col-span-7 grid grid-cols-1 sm:grid-cols-2 gap-px bg-white/[0.05]">
                  <div className="bg-ink p-6 md:p-8">
                    <d.icon className="w-5 h-5 text-gold" />
                    <div className="mt-6 overline">What sits inside</div>
                    <ul className="mt-4 space-y-3 text-sm text-zinc-300">
                      {d.components.map((c) => (
                        <li key={c} className="flex items-start gap-3">
                          <span className="mt-2 inline-block w-1 h-1 bg-gold flex-shrink-0" />
                          <span>{c}</span>
                        </li>
                      ))}
                    </ul>
                  </div>
                  <div className="bg-ink p-6 md:p-8">
                    <div className="overline">Outcome</div>
                    <p className="mt-6 text-zinc-200 leading-relaxed text-[15px]">{d.outcome}</p>
                  </div>
                </div>
              </article>
            </Reveal>
          ))}
        </div>
      </section>

      {/* OFFER STACK */}
      <section className="section-y bg-[#070707] border-y border-white/[0.05]" data-testid="wwd-stack">
        <div className="container-x grid grid-cols-1 lg:grid-cols-12 gap-12 items-start">
          <div className="lg:col-span-5">
            <SectionHeading
              eyebrow="The offer stack"
              title="How clients typically engage with us."
              description="A clear path from entry to a long-term retained relationship, one offer at a time."
            />
          </div>
          <div className="lg:col-span-7 space-y-px bg-white/[0.05]">
            {STACK.map((s) => (
              <div
                key={s.offer}
                className="bg-ink p-6 md:p-8 grid grid-cols-12 gap-4 items-start"
                data-testid={`wwd-stack-${s.stage.toLowerCase().replace(/\s+/g, "-")}`}
              >
                <div className="col-span-12 sm:col-span-3 overline">{s.stage}</div>
                <div className="col-span-12 sm:col-span-9">
                  <div className="font-display text-lg md:text-xl tracking-tight">{s.offer}</div>
                  <div className="mt-1 text-[11px] uppercase tracking-[0.18em] text-zinc-500">{s.div}</div>
                  <p className="mt-3 text-zinc-400 leading-relaxed text-[15px]">{s.note}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* FAQ */}
      <section className="section-y" data-testid="wwd-faq-section">
        <div className="container-x grid grid-cols-1 lg:grid-cols-12 gap-12">
          <div className="lg:col-span-5">
            <SectionHeading
              eyebrow="FAQ"
              title="On the firm."
              description="The questions every smart operator asks before engaging us."
            />
          </div>
          <div className="lg:col-span-7">
            <FAQ items={FAQS} testIdPrefix="wwd-faq" />
          </div>
        </div>
      </section>

      <CTABanner />
    </>
  );
}
